// The attach panel (docs/superpowers/specs/2026-10-03-attach-panel-design.md). On a phone the + swaps the keyboard for a panel
// of ways to attach, in the keyboard's place and at its height, as WhatsApp's does: the message box stays where it is, and the
// keyboard icon swaps them back. Each way is an entry in ATTACH_OPTIONS, opening its own file input; inside the iOS app the page
// first asks the app for the matching picker, so Photos opens the photo library directly (native/picker).

import { useCallback, useEffect, useRef, useState, type PointerEvent, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { keyboardHeightNow, keyboardRoom, reportPanel } from '../../app/viewportHeight'
import { Icon, type IconName } from '../../components/Icon'
import { classifyMovement } from '../../hooks/gestures/recognisers'
import { DOCUMENT_ACCEPT, MEDIA_ACCEPT } from '../../lib/files'
import { askForPicker, onPickFailures, preparePicker, type PickerKind } from '../../native/picker'
import { toast } from '../../state/toasts'

interface AttachOption {
  kind: PickerKind
  label: string
  icon: IconName
  accept: string
  multiple: boolean
  capture?: 'environment'
}

export const ATTACH_OPTIONS: readonly AttachOption[] = [
  { kind: 'photos', label: 'Photos', icon: 'image', accept: MEDIA_ACCEPT, multiple: true },
  { kind: 'camera', label: 'Camera', icon: 'camera', accept: 'image/*,video/*', multiple: false, capture: 'environment' },
  { kind: 'documents', label: 'Document', icon: 'file', accept: DOCUMENT_ACCEPT, multiple: true },
]

/** The keyboard's curve takes this long (styles/layout.css): the panel slides away for as long. */
const MOTION_MS = 350
/** How long the keyboard has to come up once the message box takes focus: inside the app it starts within 100 ms. */
const KEYBOARD_WITHIN_MS = 600
/** A drag this far down the panel closes it. */
const SWIPE_CLOSE = 48

/**
 * closed; open; leaving: sliding down, the screen given back; covered: the keyboard rising over it in its place, the panel
 * taken away once the keyboard is up.
 */
export type AttachPanelPhase = 'closed' | 'open' | 'leaving' | 'covered'

export interface AttachPanelControl {
  phase: AttachPanelPhase
  height: number
  /** Rising from the bottom, rather than found under a keyboard going down. */
  rise: boolean
  open(): void
  close(): void
  /** The keyboard back in the panel's place; `focus` gives the message box focus, which brings it. */
  toKeyboard(focus: boolean): void
}

export function useAttachPanel(textareaRef: RefObject<HTMLTextAreaElement | null>): AttachPanelControl {
  const [phase, setPhase] = useState<AttachPanelPhase>('closed')
  const [height, setHeight] = useState(0)
  const [rise, setRise] = useState(false)
  // Read by handlers that run before a render: the message box's focus while the keyboard icon gives it focus.
  const phaseRef = useRef<AttachPanelPhase>('closed')
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const go = useCallback((next: AttachPanelPhase) => {
    phaseRef.current = next
    setPhase(next)
  }, [])

  const open = useCallback(() => {
    clearTimeout(timer.current)
    const box = textareaRef.current
    const typing = box !== null && document.activeElement === box
    const room = keyboardRoom()
    reportPanel(room)
    setHeight(room)
    setRise(!typing)
    go('open')
    preparePicker()
    // The keyboard goes; the panel already holds its room, so nothing above it moves.
    if (typing) box.blur()
  }, [go, textareaRef])

  const close = useCallback(() => {
    if (phaseRef.current === 'closed' || phaseRef.current === 'leaving') return
    clearTimeout(timer.current)
    reportPanel(0)
    go('leaving')
    timer.current = setTimeout(() => go('closed'), MOTION_MS)
  }, [go])

  const toKeyboard = useCallback(
    (focus: boolean) => {
      if (phaseRef.current !== 'open') return
      clearTimeout(timer.current)
      go('covered')
      if (focus) textareaRef.current?.focus()
      // The keyboard rises over the panel and takes its room; one that never comes (a keyboard attached to the phone, a page
      // that may not bring it up without a tap) leaves the panel to slide away instead.
      timer.current = setTimeout(() => {
        if (keyboardHeightNow() > 0) {
          reportPanel(0)
          go('closed')
        } else {
          phaseRef.current = 'open'
          close()
        }
      }, KEYBOARD_WITHIN_MS)
    },
    [close, go, textareaRef],
  )

  // Tapping the messages, or Escape, closes it.
  useEffect(() => {
    if (phase !== 'open') return
    const outside = (event: Event) => {
      const target = event.target
      if (target instanceof Element && target.closest('.attach-panel, .composer-area')) return
      close()
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }
    document.addEventListener('pointerdown', outside, true)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('pointerdown', outside, true)
      document.removeEventListener('keydown', escape)
    }
  }, [phase, close])

  // Leaving the conversation gives the keyboard's room back.
  useEffect(
    () => () => {
      clearTimeout(timer.current)
      if (phaseRef.current !== 'closed') reportPanel(0)
    },
    [],
  )

  // An item the app couldn't load (an iCloud photo that wouldn't download) is said, while the rest attach.
  useEffect(
    () =>
      onPickFailures((count) =>
        toast(count === 1 ? "One item couldn't be added." : `${count} items couldn't be added.`, { tone: 'error' }),
      ),
    [],
  )

  return { phase, height, rise, open, close, toKeyboard }
}

interface PanelProps {
  panel: AttachPanelControl
  onFiles: (files: File[]) => void
}

export function AttachPanel({ panel, onFiles }: PanelProps) {
  const drag = useRef<{ x: number; y: number } | null>(null)
  if (panel.phase === 'closed') return null

  const onPointerDown = (event: PointerEvent) => {
    drag.current = { x: event.clientX, y: event.clientY }
  }
  const onPointerMove = (event: PointerEvent) => {
    const start = drag.current
    if (!start) return
    const dx = event.clientX - start.x
    const dy = event.clientY - start.y
    if (classifyMovement(dx, dy) === 'vertical' && dy > SWIPE_CLOSE) {
      drag.current = null
      panel.close()
    }
  }

  return createPortal(
    <div
      className={`attach-panel ${panel.phase}${panel.rise ? ' rise' : ''}`}
      role="group"
      aria-label="Attach"
      style={{ height: panel.height }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={() => (drag.current = null)}
      onPointerCancel={() => (drag.current = null)}
    >
      <div className="attach-grid">
        {ATTACH_OPTIONS.map((option) => (
          <AttachTile key={option.kind} option={option} onFiles={onFiles} />
        ))}
      </div>
    </div>,
    document.body,
  )
}

function AttachTile({ option, onFiles }: { option: AttachOption; onFiles: (files: File[]) => void }) {
  const input = useRef<HTMLInputElement>(null)
  return (
    <>
      <button
        type="button"
        className={`attach-tile ${option.kind}`}
        onClick={async () => {
          await askForPicker(option.kind)
          input.current?.click()
        }}
      >
        <span className="attach-tile-icon" aria-hidden="true">
          <Icon name={option.icon} />
        </span>
        {option.label}
      </button>
      <input
        ref={input}
        type="file"
        hidden
        accept={option.accept}
        multiple={option.multiple}
        capture={option.capture}
        onChange={(event) => {
          const files = [...(event.target.files ?? [])]
          event.target.value = ''
          if (files.length) onFiles(files)
        }}
      />
    </>
  )
}
